var kind               = require("enyo/kind"),
    ComponentTaskQueue = require("./ComponentTaskQueue"),
    PerformanceMode    = require("./PerformanceMode");

/**
 * [PerformanceModeQueueSupport]{@link enyo-perf.PerformanceModeQueueSupport}
 * helps defer tasks if the application is in [performance mode]{@link
 * enyo-perf.PerformanceMode}.
 *
 * This mixin creates methods to add/remove tasks to a dedicated
 * [ComponentTaskQueue]{@link enyo-perf.ComponentTaskQueue} and will flush the
 * queue automatically once the application is no longer in [performance
 * mode]{@link enyo-perf.PerformanceMode}.
 *
 * @mixin enyo-perf.PerformanceModeQueueSupport
 * @protected
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-perf.PerformanceModeQueueSupport",

    /**
     * Sets up a [queue]{@link enyo-perf.ComponentTaskQueue} for tasks that get
     * deferred while the application is in [performance mode]{@link
     * enyo-perf.PerformanceMode}.
     *
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createComponent({
                name:  "performanceModeQueue",
                kind:  ComponentTaskQueue,
                guard: this.bindSafely(function() {
                    return !PerformanceMode.on;
                })
            });
            sup.apply(this, arguments);
        };
    }),

    /**
     * Sets up an observer for [performance mode]{@link
     * enyo-perf.PerformanceMode}.
     *
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            PerformanceMode.observe("on", this.bindSafely(this.performanceModeObserver));
        };
    }),

    /**
     * Flushes the [performanceModeQueue]{@link enyo-perf.ComponentTaskQueue}
     * when the application leaves in [performance mode]{@link
     * enyo-perf.PerformanceMode}.
     *
     * @param    {boolean}  wasOn  The state as it was before it changed
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @private
     */
    performanceModeObserver: function(wasOn) {
        if (wasOn && !PerformanceMode.on) {
            this.flushPerformanceModeQueue();
        }
    },

    /**
     * Add a deferred task to the [performanceModeQueue]{@link
     * enyo-perf.ComponentTaskQueue}. The deferred tasks will get executed once
     * the application leaves [performance mode]{@link
     * enyo-perf.PerformanceMode}.
     *
     * @param    {string}  method  The name of the deferred method
     * @param    {Array=}  args    The arguments to pass to the deferred method.
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @public
     */
    addToPerformanceModeQueue: function(method, args) {
        if (this.$.performanceModeQueue) {
            this.$.performanceModeQueue.addTask(method, args);
        }
    },

    /**
     * Removes a deferred task from the [performanceModeQueue]{@link
     * enyo-perf.ComponentTaskQueue}.
     *
     * @param    {string}  method  The name of the deferred method
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @public
     */
    removeFromPerformanceModeQueue: function(method) {
        if (this.$.performanceModeQueue) {
            this.$.performanceModeQueue.removeTask(method);
        }
    },

    /**
     * Flushes the [performanceModeQueue]{@link enyo-perf.ComponentTaskQueue}.
     *
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @public
     */
    flushPerformanceModeQueue: function() {
        if (this.$.performanceModeQueue) {
            this.$.performanceModeQueue.flush();
        }
    },

    /**
     * Clears the [performanceModeQueue]{@link enyo-perf.ComponentTaskQueue}.
     *
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @public
     */
    clearPerformanceModeQueue: function() {
        if (this.$.performanceModeQueue) {
            this.$.performanceModeQueue.clear();
        }
    },

    /**
     * @memberof enyo-perf.PerformanceModeQueueSupport
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.clearPerformanceModeQueue();
            sup.apply(this, arguments);
        };
    })

};
